<template>
  <div class="mt-10">
    <div class="flex items-center justify-between">
      <h2 class="txt-headline-2xl-bold">{{ t('HrReach.headline') }}</h2>
      <button v-if="applications.length" class="btn-secondary-color-m" @click="exportCsv">
        {{ t('HrReach.exportCsv') }}
      </button>
    </div>

    <ProgressBar v-if="loading" color="primary" class="mt-7" />

    <div v-else-if="applications.length" class="mt-7 flex flex-col gap-4">
      <div v-for="item in applications" :key="item.id" class="overflow-hidden rounded-lg border border-main">
        <div class="flex items-center justify-between border-b border-subtle bg-surface-elevation-1 px-4 py-3">
          <div class="flex items-center gap-3 overflow-hidden">
            <span class="txt-headline-m">{{ item.firstName }} {{ item.lastName }}</span>
            <span v-if="item.job?.title" class="txt-body-m text-subtle">{{ item.job.title }}</span>
          </div>
          <span class="txt-body-s text-subtle">{{ formatDate(item.createdAt) }}</span>
        </div>

        <div class="flex flex-col">
          <div class="grid grid-cols-2 gap-x-6 gap-y-3 border-b border-subtle bg-surface-elevation-2 p-4">
            <div v-if="item.email">
              <span class="txt-label-s text-subtle">{{ t('HrReach.columns.email') }}</span>
              <p class="txt-body-m">{{ item.email }}</p>
            </div>
            <div v-if="item.phone">
              <span class="txt-label-s text-subtle">{{ t('HrReach.columns.phone') }}</span>
              <p class="txt-body-m">{{ item.phone }}</p>
            </div>
            <div v-if="item.linkedinUrl">
              <span class="txt-label-s text-subtle">{{ t('HrReach.columns.linkedin') }}</span>
              <p>
                <a :href="item.linkedinUrl" target="_blank" rel="noopener noreferrer" class="btn-link-neutral-s-underline">
                  {{ t('HrReach.viewProfile') }}
                </a>
              </p>
            </div>
          </div>

          <div v-if="item.message" class="bg-surface-elevation-2 p-4">
            <span class="txt-label-s text-subtle">{{ t('HrReach.columns.message') }}</span>
            <p class="txt-body-m">{{ item.message }}</p>
          </div>

          <div v-if="item.userProfileId" class="flex justify-end border-t border-subtle bg-surface-elevation-1 px-4 py-3">
            <button
              class="btn-secondary-neutral-s"
              :disabled="profileLoadingId === item.id"
              @click="handleViewProfile(item.userProfileId, item.id)"
            >
              {{ t('HrReach.viewVisitorProfile') }}
            </button>
          </div>
        </div>
      </div>
    </div>

    <VisitorProfileModal v-if="visitorProfileRecord" :show="showProfileModal" :visitor="visitorProfileRecord" @close="closeProfileModal" />

    <ContentCard v-if="!loading && !applications.length" class="mt-7">
      <div class="py-12 text-center">
        <p class="txt-headline-s text-subtle">{{ t('HrReach.noApplications') }}</p>
        <p class="mt-2 txt-body-m text-subtlest">{{ t('HrReach.noApplicationsDescription') }}</p>
      </div>
    </ContentCard>
  </div>
</template>

<script setup lang="ts">
import { useApolloClient } from '@vue/apollo-composable'
import { computed, ref } from 'vue'
import { useI18n } from 'vue-i18n'

import VisitorProfileModal from '@/components/VisitorProfiles/VisitorProfileModal.vue'
import { type JobInterestsByExhibitorQuery, useJobInterestsByExhibitorQuery } from '@/gql/jobs'
import { VisitorProfileDocument, type VisitorProfileQuery, type VisitorProfileQueryVariables } from '@/gql/myomr'
import { type CsvColumn, downloadCsv, generateCsv } from '@/helpers/csvExport'
import { showNotification } from '@/helpers/notificationHelper'
import { useRoleRedirect } from '@/services/roleRedirect'
import { useMeStore } from '@/stores'
import ContentCard from '@/ui/ContentCard.vue'

const ROOT_EVENT_SLUG = 'omr26'

useRoleRedirect('viewRecruitingPlus')

const { t } = useI18n()
const { client } = useApolloClient()
const meStore = useMeStore()

const partnerCompanySlug = computed(() => meStore.me?.partnerCompany?.slug ?? '')

const { result, loading } = useJobInterestsByExhibitorQuery(
  () => ({
    partnerCompanySlug: partnerCompanySlug.value,
    eventSlug: ROOT_EVENT_SLUG,
  }),
  () => ({
    clientId: 'jobs',
    enabled: !!partnerCompanySlug.value,
  }),
)

const applications = computed(() => result.value?.jobInterestsByExhibitor ?? [])

function formatDate(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString()
}

type JobInterestRow = JobInterestsByExhibitorQuery['jobInterestsByExhibitor'][number]

const csvColumns: CsvColumn<JobInterestRow>[] = [
  { header: 'Job Title', accessor: (r) => r.job?.title },
  { header: 'First Name', accessor: (r) => r.firstName },
  { header: 'Last Name', accessor: (r) => r.lastName },
  { header: 'Email', accessor: (r) => r.email },
  { header: 'Phone', accessor: (r) => r.phone },
  { header: 'LinkedIn', accessor: (r) => r.linkedinUrl },
  { header: 'Message', accessor: (r) => r.message },
  { header: 'Submitted At', accessor: (r) => r.createdAt },
]

function exportCsv() {
  const date = new Date().toISOString().split('T')[0]
  const csv = generateCsv(csvColumns, applications.value)
  downloadCsv(csv, `recruiting-plus-${ROOT_EVENT_SLUG}-${date}.csv`)
}

type VisitorProfileRecord = NonNullable<VisitorProfileQuery['visitorProfile']>

const showProfileModal = ref(false)
const visitorProfileRecord = ref<VisitorProfileRecord | null>(null)
const profileLoadingId = ref<string | null>(null)

async function handleViewProfile(userId: string, jobInterestId: string) {
  profileLoadingId.value = jobInterestId
  try {
    const { data } = await client.query<VisitorProfileQuery, VisitorProfileQueryVariables>({
      query: VisitorProfileDocument,
      variables: { userId },
      fetchPolicy: 'network-only',
    })
    const vp = data?.visitorProfile
    if (vp) {
      visitorProfileRecord.value = vp
      showProfileModal.value = true
    } else {
      showNotification(t('HrReach.visitorProfileNotFound'), 'error')
    }
  } catch {
    showNotification(t('common.errorOccurred'), 'error')
  } finally {
    profileLoadingId.value = null
  }
}

function closeProfileModal() {
  showProfileModal.value = false
}
</script>
